/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-19
 */

import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  Button,
} from '@mui/material';
import {
  People as PeopleIcon,
  Upgrade as UpgradeIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetCurrentSubscriptionQuery } from '../../features/auth/authApi';
import { useStudioPermissions } from '../../hooks/useStudioPermissions';
import { useStudioUserLimits } from '../../hooks/useSubscriptionLimits';
import { StudioRole } from '../../types/studio';

/**
 * Props for the StudioUsageCard component
 */
interface StudioUsageCardProps {
  /**
   * Studio ID to check limits for
   */
  studioId: string;
  /**
   * Current number of members in the studio
   */
  currentMemberCount: number;
  /**
   * Whether to show as a compact version
   */
  compact?: boolean;
  /**
   * Additional styling
   */
  sx?: any;
}

/**
 * Card component showing studio usage information and subscription limits
 *
 * @param props - Component props
 * @returns Studio usage card component
 *
 * @example
 * <StudioUsageCard studioId="123" currentMemberCount={15} />
 */
const StudioUsageCard: React.FC<StudioUsageCardProps> = ({
  studioId,
  currentMemberCount,
  compact = false,
  sx,
}) => {
  const { t } = useTranslation('billing');
  const navigate = useNavigate();
  const { data: subscription } = useGetCurrentSubscriptionQuery();
  const { role } = useStudioPermissions();
  const { remainingUsers, isAtUserLimit, userLimitPercent } =
    useStudioUserLimits(studioId, currentMemberCount);

  const maxUsers =
    remainingUsers === Infinity ? 0 : currentMemberCount + remainingUsers;
  const isUnlimited = remainingUsers === Infinity;
  const isApproachingLimit = userLimitPercent >= 80 && !isUnlimited;
  const isStudioOwner = role === StudioRole.OWNER;

  const handleUpgradeClick = () => {
    navigate('/pricing');
  };

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <PeopleIcon
          fontSize="small"
          color={isAtUserLimit ? 'warning' : 'primary'}
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {currentMemberCount}{' '}
          {isUnlimited
            ? t('usage.members')
            : `/ ${maxUsers} ${t('usage.members')}`}
        </Typography>
        {isAtUserLimit && (
          <Chip
            label={t('usage.atLimit')}
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
      </Box>
    );
  }

  return (
    <Card sx={{ ...sx }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            {t('usage.members')}
          </Typography>
          {(isAtUserLimit || isApproachingLimit) && isStudioOwner && (
            <Chip
              label={isAtUserLimit ? t('usage.atLimit') : t('usage.nearLimit')}
              size="small"
              color={isAtUserLimit ? 'error' : 'warning'}
              icon={<WarningIcon />}
            />
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('usage.currentUsage')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {currentMemberCount}
            </Typography>
          </Box>

          {!isUnlimited && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('usage.planLimit')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {maxUsers} {t('usage.members')}
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={Math.min(userLimitPercent, 100)}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: (() => {
                      if (isAtUserLimit) return 'error.main';
                      if (isApproachingLimit) return 'warning.main';
                      return 'success.main';
                    })(),
                  },
                }}
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mt: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {remainingUsers} {t('usage.remaining')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('usage.percentageUsed', {
                    percent: Math.round(userLimitPercent),
                  })}
                </Typography>
              </Box>
            </>
          )}

          {isUnlimited && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                ✨{' '}
                {t('usage.unlimitedTooltip', {
                  resource: t('usage.members').toLowerCase(),
                })}
              </Typography>
            </Box>
          )}
        </Box>

        {isAtUserLimit && isStudioOwner && (
          <Box
            sx={{
              p: 2,
              backgroundColor: 'warning.light',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('limits.memberLimitReached')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('limits.upgradeToInviteMore')}
            </Typography>
          </Box>
        )}

        {isApproachingLimit && !isAtUserLimit && isStudioOwner && (
          <Box
            sx={{ p: 2, backgroundColor: 'info.light', borderRadius: 1, mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {t('limits.approachingMemberLimit')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('limits.roomToGrow')}
            </Typography>
          </Box>
        )}

        {(isAtUserLimit || isApproachingLimit) && isStudioOwner && (
          <Button
            variant="contained"
            startIcon={<UpgradeIcon />}
            onClick={handleUpgradeClick}
            fullWidth
            sx={{ mt: 1 }}
          >
            {t('usage.upgradeNow')}
          </Button>
        )}

        {!isAtUserLimit && !isApproachingLimit && !isUnlimited && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: 'center', display: 'block' }}
          >
            {t('plans.on')} {subscription?.plan?.name || t('plans.free')}{' '}
            {t('plans.plan')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default StudioUsageCard;
